import { useNavigate, useParams } from "react-router-dom";
import { HOTEL_DETAILS_STEPS } from "../constants";
import { HotelStepValue, ValidityState } from "../constants";
import { useHotelDynamicDetails } from "../Pages/HotelDetails/context";

const DEFAULT_STEP = 0;

export default function useStepFlow() {
  const { validityState } = useHotelDynamicDetails();
  const [validity, setValidity] = validityState;
  const { searchId, hotelKey, hotelDetailsKey, step: stepParam } = useParams();
  const navigate = useNavigate();
  const currentStep = HOTEL_DETAILS_STEPS.find((s) => s.value === stepParam)?.number ?? DEFAULT_STEP;

  const navigateToStep = (step: number, replace = false) => {
    const value = HOTEL_DETAILS_STEPS.find((s) => s.number === step)?.value;
    if (!value) return;
    navigate(`/hotel/${searchId}/hotel/${hotelKey}/book/${hotelDetailsKey}/${value}`, { replace });
  };

  const goToStep = (step: number) => {
    const stepConfiguration = HOTEL_DETAILS_STEPS.find((option) => option.number === step);
    const nextMovementPossible = stepConfiguration?.canEnter?.(validity);
    if (!nextMovementPossible || step === currentStep) return;
    navigateToStep(step);
  };

  const validateStep = (step: HotelStepValue) => {
    const newValidity = {
      ...validity,
      [step]: true,
    };
    setValidity(newValidity);
    return newValidity;
  };
  const invalidateStep = (step: HotelStepValue) => {
    const newValidity = {
      ...validity,
      [step]: false,
    };
    setValidity(newValidity);
    return newValidity;
  };
  const canEnterStep = (step: number, overrideValidity?: ValidityState): boolean => {
    const config = HOTEL_DETAILS_STEPS.find((s) => s.number === step);
    return config?.canEnter?.(overrideValidity ?? validity) ?? false;
  };

  const goToNextStep = (overrideValidity?: ValidityState) => {
    const currentIndex = HOTEL_DETAILS_STEPS.findIndex((s) => s.number === currentStep);
    const nextStep = HOTEL_DETAILS_STEPS[currentIndex + 1];
    if (!nextStep) return;
    if (!canEnterStep(nextStep.number, overrideValidity)) return;
    navigateToStep(nextStep.number);
  };

  const ensureStepAllowed = () => {
    if (!canEnterStep(currentStep)) navigateToStep(DEFAULT_STEP, true);
  };

  const isStepAllowed = (step: number) => canEnterStep(step);

  return { goToStep, currentStep, validateStep, invalidateStep, isStepAllowed, goToNextStep, ensureStepAllowed };
}
